<template>
    <form autocomplete="off">
        <div class="row gy-3">
            <div class="col-lg-6">
                <label>Experience Type <span class="text-danger">*</span></label>
                <select class="form-select" v-model="form.type" :class="{ 'is-invalid': v$.form.type.$error }">
                    <option disabled value="">--select--</option>
                    <option v-for="(item, index) in experienceTypes" :key="index" :value="item.value">
                        {{ item.label }}
                    </option>
                </select>
                <div class="invalid-feedback" v-for="(item, index) in v$.form.type.$errors" :key="index">
                    {{ item.$message }}
                </div>
            </div>
            <div class="col-lg-6">
                <label>Institution <span class="text-danger">*</span></label>
                <input type="text" class="form-control" placeholder="e.g. Akwa Ibom State University"
                    v-model="form.institution" :class="{ 'is-invalid': v$.form.institution.$error }">
                <div class="invalid-feedback" v-for="(item, index) in v$.form.institution.$errors" :key="index">
                    {{ item.$message }}
                </div>
            </div>
            <div class="col-lg-6">
                <label>Department <span class="text-danger">*</span></label>
                <input type="text" class="form-control" placeholder="e.g. Department of Computer Science"
                    v-model="form.department" :class="{ 'is-invalid': v$.form.department.$error }">
                <div class="invalid-feedback" v-for="(item, index) in v$.form.department.$errors" :key="index">
                    {{ item.$message }}
                </div>
            </div>

            <div class="col-lg-6" v-if="isTeaching">
                <label>Level <span class="text-danger">*</span></label>
                <select class="form-select" v-model="form.level" :class="{ 'is-invalid': v$.form.level.$error }">
                    <option disabled value="">--select level--</option>
                    <option v-for="(item, index) in levelOptions" :key="index" :value="item.value">
                        {{ item.label }}
                    </option>
                </select>
                <div class="invalid-feedback" v-for="(item, index) in v$.form.level.$errors" :key="index">
                    {{ item.$message }}
                </div>
            </div>
            <div class="col-lg-6">
                <label> Duration <span class="text-danger">*</span>
                </label>
                <input type="text" class="form-control" placeholder="e.g. 5 years" v-model="form.duration"
                    :class="{ 'is-invalid': v$.form.duration.$error }">
                <div class="invalid-feedback" v-for="(item, index) in v$.form.duration.$errors" :key="index">
                    {{ item.$message }}
                </div>
            </div>
            <div class="col-lg-6" v-if="isTeaching">
                <label>Students Supervised <span class="text-danger">*</span></label>
                <CommonIncrementInput
                    v-model="form.students_supervised"
                    :min="1"
                    :max="10000"
                    :isInvalid="v$.form.students_supervised.$error"
                />
                <div class="invalid-feedback" v-for="(item, index) in v$.form.students_supervised.$errors" :key="index">
                    {{ item.$message }}
                </div>
            </div>
            <div class="col-12" v-if="isTeaching">
                <label>Courses Taught <span class="text-danger">*</span></label>
                <textarea rows="4" class="form-control"
                    placeholder="e.g. Data Structures, Database Systems, Software Engineering"
                    v-model="form.courses_taught" :class="{ 'is-invalid': v$.form.courses_taught.$error }"></textarea>
                <div class="invalid-feedback" v-for="(item, index) in v$.form.courses_taught.$errors" :key="index">
                    {{ item.$message }}
                </div>
            </div>
            <div class="col-12">
                <label>Responsibilities <span class="text-danger">*</span></label>
                <textarea rows="5" class="form-control"
                    placeholder="Describe your primary responsibilities, achievements, and duties in this role."
                    v-model="form.responsibilities"
                    :class="{ 'is-invalid': v$.form.responsibilities.$error }"></textarea>
                <div class="invalid-feedback" v-for="(item, index) in v$.form.responsibilities.$errors" :key="index">
                    {{ item.$message }}
                </div>
            </div>
            <div class="col-12">
                <div class="d-flex justify-content-end mt-3">
                    <b-button variant="primary" type="button" @click.prevent="editMode ? update() : create()">
                        {{ editMode ? 'Update' : 'Submit' }}
                    </b-button>
                </div>
            </div>
        </div>
    </form>
</template>

<script>
import { required, helpers, requiredIf } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";

export default {
    setup() {
        return { v$: useVuelidate() };
    },
    props: {
        editItem: {
            type: Object,
            default: null
        },
        editMode: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            form: {
                type: "",
                institution: "",
                department: "",
                courses_taught: "",
                level: "",
                duration: "",
                students_supervised: 0,
                responsibilities: ""
            },
            experienceTypes: [
                { value: "teaching", label: "Teaching Experience" },
                { value: "administrative", label: "Administrative Experience" }
            ],
            levelOptions: [
                { value: "certificate", label: "Certificate" },
                { value: "diploma", label: "Diploma" },
                { value: "undergraduate", label: "Undergraduate" },
                { value: "postgraduate_diploma", label: "Postgraduate Diploma" },
                { value: "masters", label: "Master's" },
                { value: "doctoral", label: "Doctoral (PhD)" },
                { value: "postdoctoral", label: "Postdoctoral" }
            ]
        }
    },
    validations() {
        return {
            form: {
                type: {
                    required: helpers.withMessage("Experience type is required", required)
                },
                institution: {
                    required: helpers.withMessage("Institution is required", required)
                },
                department: {
                    required: helpers.withMessage("Department is required", required)
                },
                responsibilities: {
                    required: helpers.withMessage("Responsibilities are required", required)
                },
                duration: {
                    required: helpers.withMessage("Duration is required",required )
                },
                level: {
                    required: helpers.withMessage("Level is required",
                        requiredIf(() => this.isTeaching)
                    )
                },
                students_supervised: {
                    required: helpers.withMessage("Students supervised is required",
                        requiredIf(() => this.isTeaching)
                    )
                },
                courses_taught: {
                    required: helpers.withMessage("Courses taught is required",
                        requiredIf(() => this.isTeaching)
                    )
                }
            }
        };
    },
    computed: {
        isTeaching() {
            return this.form.type === "teaching";
        }
    },
    methods: {
        create() {
            this.v$.$validate().then(result => {
                if (!result) {
                    this.alertError(this.v$.$errors[0].$message);
                    return;
                }
                this.$store.dispatch("changeLoaderValue", true);
                this.$http.post('/experiences/create', this.form)
                    .then(response => {
                        this.$store.dispatch("changeLoaderValue", false);
                        if (response.data.success) {
                            this.$emit("addedItem", response.data.data);
                            this.resetForm();
                        }
                    });
            });
        },
        update() {
            this.v$.$validate().then(result => {
                if (!result) {
                    this.alertError(this.v$.$errors[0].$message);
                    return;
                }
                this.$store.dispatch("changeLoaderValue", true);
                this.$http.post(`/experiences/${this.editItem.id}/update`, this.form)
                    .then(response => {
                        this.$store.dispatch("changeLoaderValue", false);
                        if (response.data.success) {
                            this.$emit("updatedItem", response.data.data);
                            this.resetForm();
                        }
                    });
            });
        },
        resetForm() {
            this.v$.$reset();
            this.$emit("closeMe");
        }
    },
    created() {
        if (this.editMode) {
            this.form = this.$filters.patchUpdatables(this.form, this.editItem);
        }
    }
}
</script>